// src/admin/components/reports/PrintReceipt.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiUserLine,
  RiBuildingLine,
  RiPrinterLine,
  RiArrowLeftLine,
  RiAlertLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { printPaymentReceipt, formatCurrency, formatDate } from '../../../models/reportModel';
import api from '../../../services/api';

const PrintReceipt = () => {
  const { paymentId } = useParams();
  const { isDarkMode } = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [orgSettings, setOrgSettings] = useState(null);

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await printPaymentReceipt(paymentId);
        setReceipt(response.receipt);
      } catch (err) {
        console.error('Error fetching receipt:', err);
        setError(err.message || 'Failed to load receipt');
      } finally {
        setLoading(false);
      }
    };

    const fetchOrgSettings = async () => {
      try {
        const response = await api.get('/organization/settings');
        if (response.data && response.data.success) {
          setOrgSettings(response.data.organization);
        }
      } catch (error) {
        console.error('Failed to fetch organization settings:', error);
      }
    };

    fetchReceipt();
    fetchOrgSettings();
  }, [paymentId]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto my-10 p-6 rounded-lg border bg-zinc-50 text-red-700 border-red-200">
        <div className="flex items-center mb-4">
          <RiAlertLine className="mr-2 text-xl" />
          <h2 className="text-xl font-semibold">Error Loading Receipt</h2>
        </div>
        <p>{error}</p>
        <button
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg flex items-center"
        >
          <RiArrowLeftLine className="mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="max-w-2xl mx-auto my-10 p-6 rounded-lg border bg-zinc-50 text-red-700 border-red-200">
        <div className="flex items-center mb-4">
          <RiAlertLine className="mr-2 text-xl" />
          <h2 className="text-xl font-semibold">Receipt Not Found</h2>
        </div>
        <p>The requested payment receipt could not be found.</p>
        <button
          onClick={handleBack}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg flex items-center"
        >
          <RiArrowLeftLine className="mr-2" />
          Go Back
        </button>
      </div>
    );
  }

  const payment = receipt.payment;
  const months = payment.months;

  return (
    <div className="max-w-2xl mx-auto my-10">
      <div className="print:hidden flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payment Receipt</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-gray-200 text-gray-700 hover:bg-gray-300 rounded-lg flex items-center"
          >
            <RiArrowLeftLine className="mr-2" />
            Back
          </button>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-zinc-500 text-white hover:bg-red-600 rounded-lg flex items-center"
          >
            <RiPrinterLine className="mr-2" />
            Print
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-8 print:shadow-none print:border-none">
        <div className="flex justify-center mb-4">
          <img
            src={orgSettings?.logoUrl || '/quadcores-light.png'}
            alt="Logo"
            className="h-20 object-contain"
          />
        </div>

        <div className="flex justify-between border-b border-gray-200 pb-6 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">RECEIPT</h2>
            <p className="text-gray-500 mt-1">#{receipt.receiptNumber}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-700">
              <span className="font-semibold">Issue Date:</span> {formatDate(new Date())}
            </p>
            <p className="text-sm text-gray-700 mt-1">
              <span className="font-semibold">Payment Date:</span> {formatDate(payment.paymentDate)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">From</h3>
            <p className="text-gray-900 font-bold">{orgSettings?.name || 'Property Management Company'}</p>
            <p className="text-gray-700 text-sm whitespace-pre-line">{orgSettings?.address || '123 Main Street\nCityville, State 12345'}</p>
            <p className="text-gray-700 text-sm">{orgSettings?.contactEmail}</p>
            <p className="text-gray-700 text-sm">Phone: {orgSettings?.contactPhone || '(123) 456-7890'}</p>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">To</h3>
            <p className="text-gray-900">{payment.tenantName}</p>
            <p className="text-gray-700 text-sm">
              {payment.buildingName}, Apt {payment.apartmentNumber}
            </p>
            <p className="text-gray-700 text-sm">Phone: {payment.tenantPhone}</p>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase mb-4">Payment Details</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left">Description</th>
                  <th scope="col" className="px-4 py-3 text-right">Period</th>
                  <th scope="col" className="px-4 py-3 text-right">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-4 capitalize">{payment.paymentType}</td>
                  <td className="px-4 py-4 text-right">
                    {formatDate(months[0])} - {formatDate(months[1])}
                  </td>
                  <td className="px-4 py-4 text-right font-medium">{formatCurrency(payment.amount)}</td>
                </tr>
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <th scope="row" colSpan="2" className="px-4 py-3 text-right font-semibold">Total</th>
                  <td className="px-4 py-3 text-right font-bold">{formatCurrency(payment.amount)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {payment.notes && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-700 uppercase mb-2">Notes</h3>
            <p className="text-gray-700 text-sm p-3 bg-gray-50 rounded-lg border border-gray-200">
              {payment.notes}
            </p>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6 text-center">
          <p className="text-gray-700 text-sm">Thank you for your payment!</p>
          <p className="text-gray-500 text-xs mt-2">This receipt was generated automatically.</p>
        </div>
      </div>
    </div>
  );
};

export default PrintReceipt;